import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Search, MapPin, School, Users, Building, Loader2, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, ToggleLeft, ToggleRight, X, MessageCircle } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool } from '@fortawesome/free-solid-svg-icons';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import schoolCache from '../utils/schoolCache';
import ReviewPopup from './ReviewPopup';
import SchoolCard from './SchoolCard';
import GroupedSchoolCard from './GroupedSchoolCard';
import toast from '../utils/toast';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom school marker icons for different regions
const createCustomIcon = (color) => {
    return L.divIcon({
        html: `
            <div style="
                color: ${color}; 
                width: 48px; 
                height: 48px; 
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                font-size: 24px;
                text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.9);
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                transition: all 0.2s ease;
            " onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
                <i class="fas fa-school"></i>
            </div>
        `,
        className: 'custom-school-marker',
        iconSize: [48, 48],
        iconAnchor: [24, 24]
    });
};

const regionColors = {
    'Region 1': '#3B82F6',
    'Region 2E': '#10B981',
    'Region 2W': '#F59E0B',
    'Region 3': '#8B5CF6',
    'Region 4': '#EF4444',
    'Region 5N': '#06B6D4',
    'Region 5S': '#84CC16',
    'Greater Banjul Area': '#3B82F6',
    'West Coast Region': '#10B981',
    'North Bank Region': '#F59E0B',
    'Central River Region': '#8B5CF6',
    'Upper River Region': '#EF4444',
    'Lower River Region': '#06B6D4'
};

// Component to handle map navigation and viewport-based rendering
const MapController = ({ center, zoom, isInteractive, onMapClick, selectedSchool, onViewportChange }) => {
    const map = useMap();
    const [userHasMoved, setUserHasMoved] = useState(false);
    const [lastSelectedSchool, setLastSelectedSchool] = useState(null);

    // Only auto-navigate if user hasn't manually moved the map OR if a new school is selected
    useEffect(() => {
        if (center && selectedSchool && selectedSchool.id !== lastSelectedSchool?.id) {
            map.setView(center, zoom);
            setLastSelectedSchool(selectedSchool);
            setUserHasMoved(false); // Reset on new school selection
        }
    }, [selectedSchool?.id]);

    useMapEvents({
        click: onMapClick,
        dragstart: () => {
            setUserHasMoved(true); // User is manually moving the map
        },
        zoomstart: () => {
            setUserHasMoved(true); // User is manually zooming
        },
        moveend: () => {
            const bounds = map.getBounds();
            const zoom = map.getZoom();
            onViewportChange && onViewportChange({ bounds, zoom });
        }
    });

    // Control map interaction based on isInteractive prop
    useEffect(() => {
        if (isInteractive) {
            map.scrollWheelZoom.enable();
            map.dragging.enable();
            map.doubleClickZoom.enable();
        } else {
            map.scrollWheelZoom.disable();
            map.dragging.disable();
            map.doubleClickZoom.disable();
        }
    }, [isInteractive, map]);

    return null;
};

// Component to render markers - always show all listed schools + viewport schools
const ViewportMarkers = ({ schools, listedSchools, selectedSchool, onSchoolSelect, onShowReview, isMapInteractive, onViewportChange }) => {
    const [viewportBounds, setViewportBounds] = useState(null);
    const [zoom, setZoom] = useState(7);

    const handleViewportChange = ({ bounds, zoom }) => {
        setViewportBounds(bounds);
        setZoom(zoom);
        onViewportChange && onViewportChange({ bounds, zoom });
    };

    // Always render markers for schools currently listed in the sidebar
    const listedSchoolsWithCoords = (listedSchools || []).filter(school =>
        school && school.id && typeof school.id === 'string' && school.id.length === 24 &&
        school.latitude && school.longitude
    );

    // Filter additional schools based on viewport and zoom level
    const visibleSchools = schools.filter(school => {
        if (!school || !school.id || typeof school.id !== 'string' || school.id.length !== 24 || !school.latitude || !school.longitude) return false;

        // Skip if already in listed schools
        const isListed = listedSchoolsWithCoords.some(listed => listed.id === school.id);
        if (isListed) return false;

        // At low zoom levels, show fewer markers for performance
        if (zoom < 8) {
            return Math.random() < 0.1;
        } else if (zoom < 10) {
            return Math.random() < 0.2;
        }

        // At high zoom levels, show all schools in viewport
        if (viewportBounds) {
            return viewportBounds.contains([school.latitude, school.longitude]);
        }

        return true;
    });

    // Combine listed schools with viewport schools
    const markersToRender = [...listedSchoolsWithCoords, ...visibleSchools];

    return (
        <>
            <MapController
                center={selectedSchool ? [selectedSchool.latitude, selectedSchool.longitude] : [13.4432, -15.3101]}
                zoom={selectedSchool ? 13 : 7}
                isInteractive={isMapInteractive} // Use parent's interactive state
                onMapClick={() => { }}
                selectedSchool={selectedSchool}
                onViewportChange={handleViewportChange}
            />
            <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={50}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
                iconCreateFunction={(cluster) => {
                    const count = cluster.getChildCount();
                    const size = count < 10 ? 'small' : count < 100 ? 'medium' : 'large';
                    const colors = {
                        small: '#3B82F6',
                        medium: '#10B981',
                        large: '#EF4444'
                    };
                    return L.divIcon({
                        html: `<div style="
                            background-color: ${colors[size]};
                            color: white;
                            border-radius: 50%;
                            width: 40px;
                            height: 40px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: bold;
                            font-size: 14px;
                            border: 2px solid white;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                        ">${count}</div>`,
                        className: 'custom-cluster-icon',
                        iconSize: L.point(40, 40)
                    });
                }}
            >
                {markersToRender.map((school) => {
                    // Get cached metadata for instant display
                    const cachedMetadata = schoolCache.getSchoolMetadata(school.id);
                    const stats = cachedMetadata.stats;
                    const studentCount = cachedMetadata.studentCount;

                    return (
                        <Marker
                            key={school.id}
                            position={[school.latitude, school.longitude]}
                            icon={createCustomIcon(regionColors[school.region] || '#6B7280')}
                            eventHandlers={{
                                click: (e) => {
                                    e.originalEvent.stopPropagation();
                                    onSchoolSelect(school);
                                }
                            }}
                        >
                            <Popup
                                className="custom-popup"
                                closeButton={true}
                                closeOnClick={false}
                                autoClose={false}
                                autoPan={true}
                                autoPanPadding={[50, 50]}
                            >
                                <div className="p-3 min-w-[280px]">
                                    <h3 className="font-bold text-lg mb-2">{school.name}</h3>
                                    <div className="space-y-1 text-sm">
                                        <p><strong>Region:</strong> {school.region}</p>
                                        <p><strong>District:</strong> {school.district}</p>
                                        <p><strong>Type:</strong> {school.type}</p>
                                        <p><strong>Management:</strong> {school.management}</p>
                                        {school.classification && (
                                            <p><strong>Classification:</strong> {school.classification}</p>
                                        )}
                                        {studentCount > 0 && (
                                            <p><strong>Students Enrolled:</strong> {studentCount}</p>
                                        )}
                                        {stats.reviewCount > 0 && (
                                            <p><strong>Rating:</strong> ⭐ {stats.averageRating.toFixed(1)} ({stats.reviewCount} reviews)</p>
                                        )}
                                        {typeof school.latitude === 'number' && typeof school.longitude === 'number' && (
                                            <p><strong>Coords:</strong> {school.latitude.toFixed(4)}, {school.longitude.toFixed(4)}</p>
                                        )}
                                        {school.address && (
                                            <p><strong>Address:</strong> {school.address}</p>
                                        )}
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onShowReview(school);
                                        }}
                                        className="mt-3 w-full bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 transition-colors"
                                    >
                                        Leave a Review
                                    </button>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MarkerClusterGroup>
        </>
    );
};

const SchoolsSection = () => {
    const [schools, setSchools] = useState([]);
    const [isMapInteractive, setIsMapInteractive] = useState(false); // Start as NON-interactive
    const [filteredSchools, setFilteredSchools] = useState([]);
    const [displayedSchools, setDisplayedSchools] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [showReviewPopup, setShowReviewPopup] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [reviewRefreshTrigger, setReviewRefreshTrigger] = useState(0);
    const [viewportBounds, setViewportBounds] = useState(null);
    const [filterViewport, setFilterViewport] = useState(false);
    const [showSchoolDetails, setShowSchoolDetails] = useState(false);
    const [inactivityTimer, setInactivityTimer] = useState(null);
    const schoolsPerPage = 20;
    const listRef = useRef(null);

    // Load schools from cache
    useEffect(() => {
        const unsubscribe = schoolCache.subscribe((cacheData) => {
            setSchools(cacheData.schools || []);
            setLoading(cacheData.isLoading);
            setError(cacheData.error);
        });

        // Load schools if not already loaded
        if (schoolCache.schools.length === 0) {
            schoolCache.loadSchools();
        } else {
            setSchools(schoolCache.schools);
            setLoading(false);
        }

        return unsubscribe;
    }, []);

    // Filter and sort schools based on search query, sorting options, and viewport
    useEffect(() => {
        // First filter out schools with invalid IDs
        let filtered = schools.filter(school => {
            return school && school.id && typeof school.id === 'string' && school.id.length === 24;
        });

        // Apply search filter
        if (searchQuery.trim()) {
            filtered = schoolCache.searchSchools(searchQuery, 1000);
        }

        // Apply viewport filter if enabled
        if (filterViewport && viewportBounds) {
            filtered = filtered.filter(school => {
                if (!school.latitude || !school.longitude) return false;
                return viewportBounds.contains([school.latitude, school.longitude]);
            });
        }

        // Apply sorting
        filtered = [...filtered].sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'region':
                    aValue = a.region.toLowerCase();
                    bValue = b.region.toLowerCase();
                    break;
                case 'district':
                    aValue = a.district.toLowerCase();
                    bValue = b.district.toLowerCase();
                    break;
                case 'type':
                    aValue = a.type.toLowerCase();
                    bValue = b.type.toLowerCase();
                    break;
                default:
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
            }

            if (sortOrder === 'asc') {
                return aValue.localeCompare(bValue);
            } else {
                return bValue.localeCompare(aValue);
            }
        });

        setFilteredSchools(filtered);
        setCurrentPage(1);
        setHasMore(true);
    }, [searchQuery, schools, sortBy, sortOrder, filterViewport, viewportBounds]);

    // Update displayed schools for pagination
    useEffect(() => {
        const startIndex = 0;
        const endIndex = currentPage * schoolsPerPage;
        const newDisplayedSchools = filteredSchools.slice(startIndex, endIndex);
        setDisplayedSchools(newDisplayedSchools);
        setHasMore(endIndex < filteredSchools.length);
    }, [filteredSchools, currentPage]);

    // School details are now preloaded via schoolCache on initial load
    // No need for additional preloading here - just use cached data

    // Handle infinite scroll
    const handleScroll = () => {
        if (!listRef.current || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            setCurrentPage(prev => prev + 1);
        }
    };

    // Auto-close panel after inactivity
    const startInactivityTimer = () => {
        // Clear existing timer
        if (inactivityTimer) {
            clearTimeout(inactivityTimer);
        }

        // Set new timer for 120 seconds
        const timer = setTimeout(() => {
            setShowSchoolDetails(false);
            setSelectedSchool(null);
        }, 120000); // 120 seconds

        setInactivityTimer(timer);
    };

    // Reset inactivity timer on user interaction
    const resetInactivityTimer = () => {
        if (showSchoolDetails) {
            startInactivityTimer();
        }
    };

    // Handle school selection from list (navigate map to school)
    const handleSchoolSelect = (school) => {
        // Set new selected school and show details
        setSelectedSchool(school);
        setIsMapInteractive(true);
        setShowSchoolDetails(true);

        // Start inactivity timer
        startInactivityTimer();

        // Move selected school to top of list
        if (school && school.id) {
            const schoolIndex = filteredSchools.findIndex(s => s.id === school.id);
            if (schoolIndex > 0) {
                const updatedFilteredSchools = [...filteredSchools];
                const [selectedSchoolData] = updatedFilteredSchools.splice(schoolIndex, 1);
                updatedFilteredSchools.unshift(selectedSchoolData);
                setFilteredSchools(updatedFilteredSchools);
            }
        }
    };

    // Handle showing review popup
    const handleShowReview = (school) => {
        console.log('SchoolsSection: handleShowReview called with school:', school?.name);
        console.log('SchoolsSection: Setting selectedSchool and showReviewPopup to true');
        setSelectedSchool(school);
        setShowReviewPopup(true);
        console.log('SchoolsSection: Review popup should now be visible');
    };

    // Handle viewport changes from map
    const handleViewportChange = ({ bounds, zoom }) => {
        setViewportBounds(bounds);
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
            }
        };
    }, [inactivityTimer]);

    // Refresh school stats after review submission
    const refreshSchoolStats = async (schoolId) => {
        try {
            console.log('Refreshing stats for school:', schoolId);

            // Fetch updated stats
            const statsResponse = await fetch(`/api/reviews/stats/${schoolId}`);
            if (statsResponse.ok) {
                const statsData = await statsResponse.json();

                // Update cache
                const metadata = schoolCache.schoolMetadata.get(schoolId);
                if (metadata) {
                    metadata.stats = statsData;
                    metadata.statsLoaded = true;
                    metadata.lastStatsUpdate = Date.now();
                }

                console.log('School stats refreshed:', statsData);
            }
        } catch (error) {
            console.error('Error refreshing school stats:', error);
        }
    };

    // Extract base name for smart grouping (remove numbers, "School", "No", etc.)
    const extractBaseName = (name) => {
        if (!name) return '';
        return name
            .replace(/\s+(School|No\.?\s*\d+|#\d+|\d+)$/i, '') // Remove "School", "No 4", "#5", etc.
            .replace(/\s+(Primary|Secondary|Elementary|High|Basic|Upper|Lower)$/i, '') // Remove school levels
            .replace(/\s+/g, ' ') // Normalize spaces
            .trim()
            .toLowerCase();
    };

    // Get the most representative name from a group of schools
    const getMostRepresentativeName = (schools) => {
        if (!schools || schools.length === 0) return '';

        // Sort by name length (prefer shorter, cleaner names)
        const sorted = [...schools].sort((a, b) => {
            const aName = a.name || '';
            const bName = b.name || '';

            // Prefer shorter names
            if (aName.length !== bName.length) {
                return aName.length - bName.length;
            }

            // If same length, prefer names without numbers
            const aHasNumber = /\d/.test(aName);
            const bHasNumber = /\d/.test(bName);

            if (aHasNumber !== bHasNumber) {
                return aHasNumber ? 1 : -1;
            }

            // Alphabetical as final tiebreaker
            return aName.localeCompare(bName);
        });

        return sorted[0].name;
    };

    // Smart grouping that handles similar names
    const groupSchoolsByName = (schools) => {
        const grouped = {};
        const processed = new Set();

        schools.forEach(school => {
            // Skip if already processed or has invalid ID
            if (processed.has(school.id) || !school || !school.id || typeof school.id !== 'string' || school.id.length !== 24) return;

            const name = school.name;
            const baseName = extractBaseName(name);

            // Find all schools with same base name
            const similarSchools = schools.filter(s => {
                if (processed.has(s.id) || !s || !s.id || typeof s.id !== 'string' || s.id.length !== 24) return false;

                const sName = s.name;
                const sBaseName = extractBaseName(sName);

                // Exact match or base name match
                return sName === name || sBaseName === baseName;
            });

            // Use the most representative name as the group key
            const groupKey = getMostRepresentativeName(similarSchools);

            // Initialize group if it doesn't exist
            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }

            // Add all similar schools to the group
            similarSchools.forEach(s => {
                grouped[groupKey].push(s);
                processed.add(s.id);
            });
        });

        return grouped;
    };

    // Get grouped schools for display
    const getGroupedSchools = () => {
        const grouped = groupSchoolsByName(displayedSchools);
        return Object.entries(grouped).map(([name, schools]) => ({
            name,
            schools,
            isGroup: schools.length > 1
        }));
    };

    // Handle review submission
    const handleReviewSubmit = async (reviewData) => {
        try {
            console.log('Submitting review:', {
                schoolId: selectedSchool.id,
                ...reviewData
            });

            const response = await fetch('/api/reviews', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    schoolId: selectedSchool.id,
                    rating: reviewData.rating,
                    comment: reviewData.comment,
                    firstName: reviewData.firstName,
                    lastName: reviewData.lastName,
                    email: reviewData.email
                })
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log('Review submission response:', responseData);

                // Clear cache for this school to force refresh
                const metadata = schoolCache.schoolMetadata.get(selectedSchool.id);
                if (metadata) {
                    metadata.statsLoaded = false; // Mark stats as needing reload
                }

                // Trigger refresh of reviews in modals
                setReviewRefreshTrigger(prev => prev + 1);

                // Refresh school stats for the map
                await refreshSchoolStats(selectedSchool.id);

                setShowReviewPopup(false);
                toast.success('Review submitted successfully!');
                return true;
            } else if (response.status === 429) {
                toast.error('Too many requests. Please wait a moment and try again.');
                return false;
            } else {
                try {
                    const responseData = await response.json();
                    console.error('Failed to submit review:', responseData);
                    if (responseData.details && Array.isArray(responseData.details)) {
                        responseData.details.forEach(detail => {
                            toast.error(detail);
                        });
                    } else {
                        toast.error(responseData.error || 'Failed to submit review');
                    }
                } catch (parseError) {
                    console.error('Error parsing review response:', parseError);
                    toast.error('Server error. Please try again later.');
                }
                return false;
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            toast.error('Error submitting review. Please try again.');
            return false;
        }
    };

    if (loading) {
        return (
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <div className="flex items-center justify-center h-96">
                        <div className="text-center">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
                            <p className="text-gray-600">Loading schools...</p>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <div className="flex items-center justify-center h-96">
                        <div className="text-center">
                            <p className="text-red-600 mb-4">Error loading schools: {error}</p>
                            <button
                                onClick={() => schoolCache.refresh()}
                                className="btn-primary"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-gray-50 py-0">
            <div className="w-full">

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="w-full h-[750px] md:h-[875px] lg:h-[1000px] flex flex-col lg:flex-row px-4 relative"
                >
                    {/* School List Sidebar */}
                    <div className="w-auto lg:w-96 xl:w-[28rem] bg-white rounded-l-lg shadow-lg overflow-hidden flex flex-col relative">
                        <div className="p-3 border-b border-gray-200 flex-shrink-0">
                            <div className="relative mb-2">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search schools..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                />
                            </div>

                            {/* Filter Viewport Toggle */}
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => setFilterViewport(!filterViewport)}
                                        className={`flex items-center space-x-2 px-2 py-1 rounded text-xs transition-colors ${filterViewport
                                            ? 'bg-primary-100 text-primary-700'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {filterViewport ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                        <span>Filter Viewport: {filterViewport ? 'ON' : 'OFF'}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Sorting Controls */}
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-600">
                                    {filteredSchools.length} schools
                                </p>
                                <div className="flex items-center space-x-1">
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="text-xs border border-gray-300 rounded px-1 py-1 focus:ring-1 focus:ring-primary-500"
                                    >
                                        <option value="name">Name</option>
                                        <option value="region">Region</option>
                                        <option value="district">District</option>
                                        <option value="type">Type</option>
                                    </select>
                                    <button
                                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                        className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                                        title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                                    >
                                        {sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div
                            ref={listRef}
                            className="flex-1 overflow-y-auto custom-scrollbar p-4"
                            onScroll={handleScroll}
                        >
                            {getGroupedSchools().map((group, index) => (
                                <div key={group.isGroup ? group.name : group.schools[0].id} className="mb-0">
                                    {group.isGroup ? (
                                        <GroupedSchoolCard
                                            schools={group.schools}
                                            isSelected={selectedSchool?.name === group.name}
                                            onClick={handleSchoolSelect}
                                            onShowReview={handleShowReview}
                                            refreshTrigger={reviewRefreshTrigger}
                                        />
                                    ) : (
                                        <SchoolCard
                                            school={group.schools[0]}
                                            isSelected={selectedSchool?.id === group.schools[0].id}
                                            onClick={() => handleSchoolSelect(group.schools[0])}
                                            onShowReview={handleShowReview}
                                            refreshTrigger={reviewRefreshTrigger}
                                        />
                                    )}
                                </div>
                            ))}

                            {hasMore && (
                                <div className="p-3 text-center">
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Map */}
                    <div className="flex-1 bg-white rounded-r-lg shadow-lg overflow-hidden relative">
                        {/* Overlay when map is not interactive */}
                        {!isMapInteractive && (
                            <div
                                className="absolute inset-0 z-[1000] flex items-center justify-center bg-black/10 cursor-pointer backdrop-blur-[1px]"
                                onClick={() => setIsMapInteractive(true)}
                            >
                                <div className="bg-white px-6 py-3 rounded-lg shadow-xl">
                                    <p className="text-gray-700 font-medium">Click to interact with map</p>
                                </div>
                            </div>
                        )}

                        <MapContainer
                            center={[13.4432, -15.3101]} // Center of The Gambia
                            zoom={7}
                            style={{ height: '100%', width: '100%' }}
                            className="z-0"
                            scrollWheelZoom={false}
                            dragging={false}
                            doubleClickZoom={false}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />

                            <ViewportMarkers
                                schools={schools}
                                listedSchools={displayedSchools}
                                selectedSchool={selectedSchool}
                                onSchoolSelect={handleSchoolSelect}
                                onShowReview={handleShowReview}
                                isMapInteractive={isMapInteractive}
                                onViewportChange={handleViewportChange}
                            />
                        </MapContainer>
                    </div>

                    {/* School Details Panel - Extension of List */}
                    <motion.div
                        initial={false}
                        animate={{
                            x: showSchoolDetails ? 0 : 400,
                            opacity: showSchoolDetails ? 1 : 0
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 30,
                            duration: 0.3
                        }}
                        className="absolute top-0 w-96 h-full bg-white shadow-xl border-l border-gray-200 z-30 flex flex-col"
                        style={{
                            right: showSchoolDetails ? '0' : '-400px',
                            transform: showSchoolDetails ? 'translateX(0)' : 'translateX(100%)',
                            pointerEvents: showSchoolDetails ? 'auto' : 'none'
                        }}
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-primary-600 text-white">
                            <h3 className="text-lg font-semibold">School Details</h3>
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                    setShowSchoolDetails(false);
                                    setSelectedSchool(null);
                                    if (inactivityTimer) {
                                        clearTimeout(inactivityTimer);
                                        setInactivityTimer(null);
                                    }
                                }}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </motion.button>
                        </div>

                        {/* Content */}
                        {selectedSchool && (
                            <motion.div
                                key={selectedSchool.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                                className="flex-1 overflow-y-auto p-4"
                                onMouseMove={resetInactivityTimer}
                                onScroll={resetInactivityTimer}
                            >
                                <div className="space-y-6">
                                    {/* School Header */}
                                    <div className="text-center">
                                        <motion.div
                                            initial={{ scale: 0.8 }}
                                            animate={{ scale: 1 }}
                                            transition={{ duration: 0.3, delay: 0.2 }}
                                            className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3"
                                        >
                                            <School size={32} className="text-primary-600" />
                                        </motion.div>
                                        <h4 className="text-xl font-bold text-gray-900 mb-2">{selectedSchool.name}</h4>
                                        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                                            <MapPin size={14} />
                                            <span>{selectedSchool.district}, {selectedSchool.region}</span>
                                        </div>
                                    </div>

                                    {/* School Information */}
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                                                <Building size={16} className="mr-2 text-primary-600" />
                                                School Information
                                            </h5>
                                            <div className="space-y-3 text-sm">
                                                <div className="flex items-center space-x-3">
                                                    <School size={14} className="text-gray-400" />
                                                    <div>
                                                        <span className="text-gray-500">Type:</span>
                                                        <span className="ml-2 font-medium text-gray-900">{selectedSchool.type}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <Users size={14} className="text-gray-400" />
                                                    <div>
                                                        <span className="text-gray-500">Management:</span>
                                                        <span className="ml-2 font-medium text-gray-900">{selectedSchool.management}</span>
                                                    </div>
                                                </div>
                                                {selectedSchool.classification && (
                                                    <div className="flex items-center space-x-3">
                                                        <Building size={14} className="text-gray-400" />
                                                        <div>
                                                            <span className="text-gray-500">Classification:</span>
                                                            <span className="ml-2 font-medium text-gray-900">{selectedSchool.classification}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Address */}
                                        {selectedSchool.address && (
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                                                    <MapPin size={16} className="mr-2 text-primary-600" />
                                                    Address
                                                </h5>
                                                <p className="text-sm text-gray-600">{selectedSchool.address}</p>
                                            </div>
                                        )}

                                        {/* Location Coordinates */}
                                        {selectedSchool.latitude && selectedSchool.longitude && (
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                                                    <MapPin size={16} className="mr-2 text-primary-600" />
                                                    Coordinates
                                                </h5>
                                                <p className="text-sm text-gray-600 font-mono">
                                                    {selectedSchool.latitude.toFixed(6)}, {selectedSchool.longitude.toFixed(6)}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="space-y-3 pt-4">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => {
                                                handleShowReview(selectedSchool);
                                                resetInactivityTimer();
                                            }}
                                            className="w-full bg-primary-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
                                        >
                                            <MessageCircle size={16} />
                                            <span>Write Review</span>
                                        </motion.button>

                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => {
                                                setShowSchoolDetails(false);
                                                setSelectedSchool(null);
                                                if (inactivityTimer) {
                                                    clearTimeout(inactivityTimer);
                                                    setInactivityTimer(null);
                                                }
                                            }}
                                            className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                                        >
                                            Close Panel
                                        </motion.button>
                                    </div>

                                    {/* Auto-close indicator */}
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400">
                                            Panel will auto-close after 2 minutes of inactivity
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>

                {/* Removed footer spacer to ensure map touches page footer */}
            </div>

            {/* Review Popup */}
            {showReviewPopup && selectedSchool && (
                <ReviewPopup
                    school={selectedSchool}
                    onClose={() => setShowReviewPopup(false)}
                    onSubmit={handleReviewSubmit}
                />
            )}
        </section>
    );
};

export default SchoolsSection;
